import { DataTypeColumn } from 'components/DataExpansionComponents/Common/commonColumnHelpers'
import { sequencePhase, WORKFLOW_STATUS, WorkflowStatus } from 'models/campaign'

export const SEQUENCE_COLUMNS: Record<string, DataTypeColumn> = {
  seq_membership_status: {
    // "Sequence Status", not bare "Status": the header must visibly pair with
    // the "Sequence Status" filter (FIR-7974 — bare "Status" left users
    // guessing which filter drives this column).
    header: 'Sequence Status',
    group: 'Sequencing',
  },
  seq_scheduled_emails: {
    header: 'Scheduled Emails',
    group: 'Sequencing',
  },
  seq_emails_sent: {
    header: 'Sent',
    group: 'Sequencing',
  },
  seq_replies: {
    header: 'Replies',
    group: 'Sequencing',
  },
  seq_last_email: {
    header: 'Last Email',
    group: 'Sequencing',
  },
  seq_sequence_step: {
    header: 'Sequence Step',
    group: 'Sequencing',
  },
}

export const SEQUENCE_SORTABLE_FIELDS = [
  'seq_emails_sent',
  'seq_last_email',
  'seq_membership_status',
  'seq_scheduled_emails',
  'seq_replies',
]

/** Sentinel display status for contacts on a campaign that hasn't launched. */
export const AWAITING_LAUNCH_STATUS = 'awaiting_launch'

/**
 * Sequence statuses that mean "in the campaign but EmailBison hasn't
 * individually processed this contact yet." The queued vs never-contacted
 * distinction is internal EmailBison timing (has it built the per-step email
 * rows), not a user-meaningful outcome, so both fold into the phase-
 * appropriate label. `not_in_sequence` (null) is handled separately: it has
 * no membership, so it only folds pre-launch (joins at launch), never while
 * running (there it's genuinely "Not in Sequence" and addable).
 */
const NOT_YET_PROCESSED_STATUSES: ReadonlySet<string> = new Set([
  'queued',
  'never_contacted',
])

/**
 * Collapse the sequence status for display, keyed on the campaign's lifecycle
 * phase (models/campaign.ts sequencePhase). Real outcomes — bounced, replied,
 * unsubscribed, removed — always pass through in every phase. Notably
 * `bounced` is reachable pre-launch (EmailBison verification bounces a lead
 * with zero sends), so it must never be hidden as "Awaiting launch".
 *
 * - not_launched (draft, contacts_requested, ready_to_launch, scheduled):
 *   nothing has sent, so queued / never-contacted / not-in-sequence all read
 *   "Awaiting launch."
 * - running (active, paused): a contact in the campaign is "Active" (or
 *   "Paused" if the campaign is paused) whether or not EmailBison has built
 *   their emails yet; a contact with no membership stays "Not in Sequence."
 * - completed / unknown: the real status (history / safe default).
 */
export function displayedSequenceStatus(
  status: string | null | undefined,
  campaignWorkflowStatus: WorkflowStatus | null | undefined
): string | null | undefined {
  const phase = sequencePhase(campaignWorkflowStatus)

  if (phase === 'not_launched') {
    if (!status || NOT_YET_PROCESSED_STATUSES.has(status))
      return AWAITING_LAUNCH_STATUS
    return status
  }

  if (phase === 'running') {
    if (status && NOT_YET_PROCESSED_STATUSES.has(status)) {
      // Show the same running-state chip a processed contact gets, so the
      // internal not-yet-built distinction isn't surfaced.
      return campaignWorkflowStatus === WORKFLOW_STATUS.PAUSED
        ? 'paused'
        : 'in_sequence'
    }
    return status
  }

  // completed or unknown workflow status: never collapse.
  return status
}

export interface SequenceStatusFilterOption {
  value: string
  label: string
}

// Filter values map 1:1 to the sequence_progress backend filter (server
// contacts/sequence_filters.py). `awaiting_launch` is the composite the
// backend expands to queued | never_contacted | not_in_sequence — the same
// fold displayedSequenceStatus applies, so filtering matches the chips.
const FILTER_OPTION: Record<string, SequenceStatusFilterOption> = {
  awaiting_launch: { value: 'awaiting_launch', label: 'Awaiting launch' },
  in_sequence: { value: 'in_sequence', label: 'Active' },
  queued: { value: 'queued', label: 'Queued' },
  never_contacted: { value: 'never_contacted', label: 'Never Contacted' },
  paused: { value: 'paused', label: 'Paused' },
  sequence_finished: { value: 'sequence_finished', label: 'Completed' },
  replied: { value: 'replied', label: 'Replied' },
  unsubscribed: { value: 'unsubscribed', label: 'Unsubscribed' },
  bounced: { value: 'bounced', label: 'Bounced' },
  removed: { value: 'removed', label: 'Removed' },
  not_in_sequence: { value: 'not_in_sequence', label: 'Not in Sequence' },
}

// Outcomes shown in every phase (see displayedSequenceStatus).
const OUTCOME_OPTIONS = [
  FILTER_OPTION.replied,
  FILTER_OPTION.bounced,
  FILTER_OPTION.unsubscribed,
  FILTER_OPTION.removed,
]

/**
 * The Sequence Status filter options for a campaign phase, matching exactly
 * the chips displayedSequenceStatus renders in that phase — no option for a
 * status the user can't see, and no missing option for one they can.
 *
 * - not_launched: the not-yet-contacted split is a single "Awaiting launch"
 *   chip, so that's the only membership option (plus the real outcomes).
 * - running: queued / never-contacted fold into the Active / Paused chip, so
 *   they aren't offered separately; no-membership stays "Not in Sequence".
 * - completed / unknown: the full historical set (nothing is folded).
 */
export function sequenceStatusFilterOptions(
  campaignWorkflowStatus: WorkflowStatus | null | undefined
): SequenceStatusFilterOption[] {
  const phase = sequencePhase(campaignWorkflowStatus)

  if (phase === 'not_launched') {
    return [FILTER_OPTION.awaiting_launch, ...OUTCOME_OPTIONS]
  }

  if (phase === 'running') {
    return [
      FILTER_OPTION.in_sequence,
      FILTER_OPTION.paused,
      FILTER_OPTION.sequence_finished,
      ...OUTCOME_OPTIONS,
      FILTER_OPTION.not_in_sequence,
    ]
  }

  // completed / unknown: show every real status.
  return [
    FILTER_OPTION.in_sequence,
    FILTER_OPTION.queued,
    FILTER_OPTION.never_contacted,
    FILTER_OPTION.paused,
    FILTER_OPTION.sequence_finished,
    ...OUTCOME_OPTIONS,
    FILTER_OPTION.not_in_sequence,
  ]
}
